'use client';import { type Reservation } from '@parkingbase/ecommerce-api-demo';


import React, { forwardRef, useRef, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';

import { HeaderProps, NavItem } from './Header.types';
import {
  headerClasses,
  leftSectionClasses,
  rightSectionClasses,
  logoClasses,
  mobileTitleClasses,
  navLinkClasses,
  desktopNavClasses,
  mobileGuestNavClasses,
  avatarWrapperClasses,
} from './Header.style';
import { Avatar } from '../../ui/avatar/Avatar';
import { AvatarVariant, AvatarSize } from '../../ui/avatar/Avatar.types';
import { ActiveSessionIndicator } from './active-session-indicator';
import { ActiveSessionsPopup } from './active-sessions-popup';
import { ReservationPassModal } from '@/components/myaccount/reservation-pass-modal';
import { ReleaseRefreshBanner } from '@/components/shared/release-refresh-banner';
import { useReleaseCheck } from '@/hooks/useReleaseCheck';
import { cn } from '@/lib/styleUtils';
import { Link, usePathname } from '@/lib/app-router';

const NavLink = ({
  item,
  t,
  dataCy,
}: {
  item: NavItem;
  t: ReturnType<typeof useTranslations<'Navigation'>>;
  dataCy?: string;
}) => {
  const label = item.displayLabel ?? t(item.labelKey as Parameters<typeof t>[0]);
  const cy = item.dataCy ?? dataCy;
  if (item?.onClick) {
    return (
      <button type="button" className={navLinkClasses} onClick={item.onClick} data-cy={cy}>
        {label}
      </button>
    );
  }
  return (
    <Link href={item?.href || '#'} className={navLinkClasses} data-cy={cy}>
      {label}
    </Link>
  );
};

export const Header = forwardRef<HTMLElement, HeaderProps>(
  (
    {
      logo,
      logoHref = '/',
      mobileTitle,
      primaryNavItems = [],
      secondaryNavItems = [],
      mobileGuestAction,
      user,
      onAvatarClick,
      avatarRef,
      isMenuOpen,
      activeSessionIndicator,
      activeSessionsPopup,
      className,
    },
    ref
  ) => {
    const t = useTranslations('Navigation');
    const pillRef = useRef<HTMLElement>(null);
    useReleaseCheck();

    // The logo always lands the user on a fresh copy of its destination. A <Link>
    // to the route you're already on is a no-op in the App Router, so on the
    // destination itself the click is handled here instead: re-enter the current
    // path so the page starts clean.
    // window.location.pathname is already the base-resolved path (/ or
    // /c/{companyId}), so this stays correct for company-scoped tenants.
    // Modifier clicks fall through to the Link so open-in-new-tab keeps working.
    //
    // The re-entered url keeps `logoHref`'s OWN query string rather than dropping every param
    // (PB-4971). A scan url carries the company and direction as params (`/z/412?c=ACME&d=i`),
    // and re-entering the bare path discards them — after which the scan can no longer resolve
    // which company the device belongs to, turning a "start this over" click into a dead end.
    // Callers wanting the old drop-the-query behaviour pass a `logoHref` with no query, which is
    // what `/` (the default, and so every pre-existing caller) is.
    const pathname = usePathname();
    const handleLogoClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        const [logoPath, logoQuery] = logoHref.split('?');
        if (pathname !== logoPath) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.location.assign(
          logoQuery ? `${window.location.pathname}?${logoQuery}` : window.location.pathname
        );
      },
      [pathname, logoHref]
    );

    // View Pass modal for transient parking-history tickets — opened from the
    // Active Sessions popup instead of routing to the touchless screen.
    const [passReservation, setPassReservation] = useState<Reservation | null>(null);
    const handleViewPass = useCallback((reservation: Reservation) => {
      setPassReservation(reservation);
    }, []);
    const handleClosePass = useCallback(() => setPassReservation(null), []);

    return (
      <>
      <header ref={ref} className={cn(headerClasses, className)}>
        {/* Left Section: Logo */}
        <div className={leftSectionClasses}>
          {/* Logo - hidden on mobile if mobileTitle is provided */}
          <Link
            href={logoHref}
            onClick={handleLogoClick}
            className={cn(logoClasses, mobileTitle && 'tw:hidden tw:md:flex')}
            data-cy="header-logo-link"
          >
            {logo}
          </Link>

          {/* Mobile Title - shown on mobile when provided */}
          {mobileTitle && (
            <span className={mobileTitleClasses} data-cy="header-mobile-title">
              {mobileTitle}
            </span>
          )}

        </div>

        {/* Center: Active Session Indicator */}
        {activeSessionIndicator && (
          <ActiveSessionIndicator ref={pillRef} {...activeSessionIndicator} />
        )}
        {activeSessionsPopup && (
          <ActiveSessionsPopup
            open={activeSessionsPopup.open}
            onClose={activeSessionsPopup.onClose}
            sessions={activeSessionsPopup.sessions}
            anchorRef={pillRef}
            onViewPass={handleViewPass}
          />
        )}

        {/* Right Section: desktop nav + avatar */}
        <div className={rightSectionClasses}>
          {/* Primary and secondary items share ONE nav so every pair sits at the same
              gap. As two sibling navs the seam between them fell back to this
              section's smaller gap, leaving Book Parking half as far from Parking
              Validation as the rest of the row. The avatar stays outside so it keeps
              the tighter section spacing. Hidden on mobile. */}
          {primaryNavItems.length + secondaryNavItems.length > 0 && (
            <nav className={desktopNavClasses} aria-label={t('mainNavigation')}>
              {primaryNavItems.map((item, index) => (
                <NavLink
                  key={`primary-${index}`}
                  item={item}
                  t={t}
                  dataCy="header-primary-nav-link"
                />
              ))}
              {secondaryNavItems.map((item, index) => (
                <NavLink
                  key={`secondary-${index}`}
                  item={item}
                  t={t}
                  dataCy="header-secondary-nav-link"
                />
              ))}
            </nav>
          )}

          {/* Guest action on mobile (sign-in). The nav above is md-and-up only, so
              a signed-out mobile visitor otherwise has no header sign-in and has to
              use the one in the middle of the page. Ignored once signed in — those
              users get the avatar below. */}
          {!user && mobileGuestAction && (
            <nav className={mobileGuestNavClasses}>
              <NavLink item={mobileGuestAction} t={t} dataCy="header-mobile-sign-in-link" />
            </nav>
          )}

          {/* User Avatar */}
          {user && (
            <button
              ref={avatarRef}
              type="button"
              className={avatarWrapperClasses}
              onClick={onAvatarClick}
              aria-label={t('userMenuFor', { name: user.name })}
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
              data-cy="header-avatar-button"
            >
              <Avatar
                name={user.name}
                initials={user.initials}
                variant={AvatarVariant.accent}
                size={AvatarSize.small}
                isOutline={true}
              />
            </button>
          )}
        </div>
      </header>
      <ReleaseRefreshBanner />
      {passReservation && (
        <ReservationPassModal
          open={!!passReservation}
          reservation={passReservation}
          variant="ticket"
          onClose={handleClosePass}
        />
      )}
      </>
    );
  }
);

Header.displayName = 'Header';
